import { useState, useEffect } from "react";
export interface ContentfulEntryLink {
    sys: {
        type: string;
        linkType: string;
        id: string;
    };
}

export interface ContentfulEntry<T> {
    sys: {
        id: string;
    };
    fields: T;
}

export interface ContentfulPricingPlan {
    product?: ContentfulEntryLink;
    periodUnit?: string;
    periodCount?: number;
    status?: string;
    edition?: string;
    configuration: PricingPlan[];
    pricingPlanId: string;
    sourceSequence?: number;
    source?: string;
    type?: string;
}

export interface ContentfulProduct {
    name?: string;
    platform?: ContentfulEntryLink;
    catalogItemId?: string;
    description?: string;
    productFamily?: string;
    productType?: string;
    unitLabel?: string;
    billingType?: string;
    vendor?: ContentfulEntryLink;
    parentProducts?: ContentfulEntryLink[];
    key: string;
    sourceSequence?: number;
    source?: string;
    defaultPricingPlanMonthly?: ContentfulEntryLink;
    defaultPricingPlanYearly?: ContentfulEntryLink;
    parentProductName?: string;
    defaultStdPricingPlanMonthly?: ContentfulEntryLink;
    defaultStdPricingPlanYearly?: ContentfulEntryLink;
    defaultFreePricingPlanMonthly?: ContentfulEntryLink;
    defaultFreePricingPlanYearly?: ContentfulEntryLink;
    defaultPremiumPricingPlanMonthly?: ContentfulEntryLink;
    defaultPremiumPricingPlanYearly?: ContentfulEntryLink;
    defaultEnterprisePricingPlanMonthly?: ContentfulEntryLink;
    defaultEnterprisePricingPlanYearly?: ContentfulEntryLink;
    isHidden?: string;
    isFree?: string;
    isDefaultStdPricingPlanMonthlyFree?: string;
    isDefaultStdPricingPlanYearlyFree?: string;
    isDefaultFreePricingPlanMonthlyFree?: string;
    isDefaultFreePricingPlanYearlyFree?: string;
    isDefaultPremiumPricingPlanMonthlyFree?: string;
    isDefaultPremiumPricingPlanYearlyFree?: string;
}

export interface PricingPlan {
    prices: { unit_price: number; currency: "USD" | "JPY" }[];
    pricing_strategy: string;
    unit_description: string;
    unit_limit_lower: number;
    unit_limit_upper: number;
}

export interface PricingCollection {
    free?: PricingPlan[];
    standard?: PricingPlan[];
    premium?: PricingPlan[];
}

export interface AppPricingData {
    localizedPriceBlock: {
        confluence: PricingCollection;
    };
}

export class ProductCatalogClient {
    private readonly accessToken: string;
    private readonly space: string;
    private readonly environment: string;

    public constructor(
        accessToken: string,
        space: string,
        environment = "master"
    ) {
        this.accessToken = accessToken;
        this.space = space;
        this.environment = environment;
    }

    private getUrl() {
        return `https://cdn.contentful.com/spaces/${this.space}/environments/${this.environment}`;
    }

    public async getEntries(queryParams: string) {
        const url = this.getUrl();
        const response = await fetch(
            `${url}/entries?access_token=${this.accessToken}&${queryParams}`
        );
        if (response.ok) return response.json();
        throw new Error("Unable to fetch prices");
    }

    /**
     *
     * @param key the `key` property of the CCP Product
     * @returns Monthly prices for each headcount bucket for the free, standard and premium tiers
     */
    public async getLocalizedPriceBlockPrices(
        key: string
    ): Promise<PricingCollection> {
        const pricingQuery = `content_type=product&select=fields.key%2Cfields.defaultStdPricingPlanMonthly%2Cfields.defaultPremiumPricingPlanMonthly%2Cfields.defaultFreePricingPlanMonthly%2Cfields.defaultEnterprisePricingPlanMonthly%2Csys.id%2Csys.type&fields.key%5Bin%5D=${key}&include=2`;
        const { includes, items } = (await this.getEntries(pricingQuery)) as {
            includes: { Entry: ContentfulEntry<ContentfulPricingPlan>[] };
            items: ContentfulEntry<ContentfulProduct>[];
        };
        if (!items.length) {
            throw new Error("No pricing plans match this key");
        }
        const parentPricingPlan =
            items[0] as ContentfulEntry<ContentfulProduct>;

        const freeMonthly = includes.Entry.find(
            (entry) =>
                entry.sys.id ===
                parentPricingPlan.fields.defaultFreePricingPlanMonthly?.sys.id
        );
        const standardMonthly = includes.Entry.find(
            (entry) =>
                entry.sys.id ===
                parentPricingPlan.fields.defaultStdPricingPlanMonthly?.sys.id
        );
        const premiumMonthly = includes.Entry.find(
            (entry) =>
                entry.sys.id ===
                parentPricingPlan.fields.defaultPremiumPricingPlanMonthly?.sys
                    .id
        );
        return {
            free: freeMonthly?.fields.configuration,
            standard: standardMonthly?.fields.configuration,
            premium: premiumMonthly?.fields.configuration
        };
    }
}

export function useLocalizedPriceData(
    key: string,
    accessToken: string,
    space: string,
    environment?: string
) {
    const [status, setStatus] = useState("idle");
    const [prices, setPrices] = useState<PricingCollection>();
    const client = new ProductCatalogClient(accessToken, space, environment);

    useEffect(() => {
        const fetchData = async () => {
            setStatus("fetching");
            const retrievedPrices = await client.getLocalizedPriceBlockPrices(
                key
            );
            setPrices(retrievedPrices);
            setStatus("complete");
        };

        fetchData();
    }, []);

    return { status, prices };
}
